// ─────────────────────────────────────────────────────────────────────────────
// ListExternalReferencesComponent
// ─────────────────────────────────────────────────────────────────────────────
// Paginated external references table with search, filter, sort, checkbox
// selection, product links, and SKU warning icons.
// Responsibilities:
//   - Dispatch LoadExternalReferences and display the paginated result
//   - Debounced keyword search, source filter dialog
//   - Server-side column sorting via sortBy/sortMode params
//   - Checkbox selection for bulk delete
//   - Clickable product links opening product detail in a new tab
//   - Warning icon on rows with missing product_id
// ─────────────────────────────────────────────────────────────────────────────

import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { AppState } from 'src/app/core/app.state';
import { DEFAULT_PAGE_SIZE } from 'src/app/config/global.config';
import { IPageCount, IPagination } from 'src/app/shared/models/page.model';
import { SharedModule } from 'src/app/shared/shared.module';
import { SelectionController } from 'src/app/shared/utils/selection-controller';
import { UiModule } from 'src/app/ui/ui.module';
import { ConfirmationPopUpComponent } from 'src/app/shared/confirmation-pop-up/confirmation-pop-up.component';
import { ProductsService } from 'src/app/features/produits/services/produits.services';
import { CustomersService } from 'src/app/features/customers/services/customers.services';

import { AddExternalReferenceComponent } from '../add-external-reference/add-external-reference.component';
import { ImportKnowledgeBaseDialogComponent } from '../import-knowledge-base-dialog/import-knowledge-base-dialog.component';
import { ExternalReferenceFilters, IExternalReference } from '../../models/external-reference.models';
import { ExternalReferenceService } from '../../services/external-reference.service';
import { FilterDialogComponent } from 'src/app/shared/components/filter-dialog/filter-dialog.component';
import { FilterDialogData, FilterFieldOption } from 'src/app/shared/components/filter-dialog/filter-dialog.models';
import {
  selectExternalReferenceItems,
  selectExternalReferenceLoading,
  selectExternalReferenceMetadata,
} from '../../store/external-references.selectors';
import {
  BulkDeleteExternalReferences,
  BulkDeleteExternalReferencesSuccess,
  CreateExternalReferenceSuccess,
  DeleteExternalReference,
  DeleteExternalReferenceSuccess,
  LoadExternalReferences,
  SetExternalReferenceFilters,
  SetExternalReferenceSearch,
  UpdateExternalReferenceSuccess,
} from '../../store/external-references.actions';

type SortableField = 'product_name' | 'sku' | 'reference_code' | 'customer_name' | 'source' | 'create_date';

@Component({
  selector: 'app-list-external-references',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, TranslateModule, SharedModule, UiModule],
  templateUrl: './list-external-references.component.html',
  styleUrls: ['./list-external-references.component.scss'],
})
export class ListExternalReferencesComponent implements OnInit, OnDestroy {
  // ── Store selectors ───────────────────────────────────────────────────────

  references$: Observable<IExternalReference[]> = this.store.select(selectExternalReferenceItems);
  metadata$: Observable<IPagination | null> = this.store.select(selectExternalReferenceMetadata);
  loading$: Observable<boolean> = this.store.select(selectExternalReferenceLoading);

  // ── Local state ───────────────────────────────────────────────────────────

  page: IPageCount = { number: 1, size: DEFAULT_PAGE_SIZE };
  keyword = '';
  filters: ExternalReferenceFilters = { source: '', customer_id: null, product_id: null, create_date_from: null, create_date_to: null };

  /** Row selection, range gestures and the tri-state header, in one object. */
  readonly selection = new SelectionController<IExternalReference>((row) => row.id);
  sortField: SortableField | '' = '';
  sortMode: 'asc' | 'desc' = 'asc';
  sortedReferences: IExternalReference[] = [];
  searchControl = new FormControl('');
  activeFilterValues: Record<string, any> = {};

  // ── Cleanup ───────────────────────────────────────────────────────────────

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    private dialog: MatDialog,
    private actions$: Actions,
    private translateService: TranslateService,
    private externalReferenceService: ExternalReferenceService,
    private productsService: ProductsService,
    private customersService: CustomersService,
  ) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    // Sync references from store (server already returns sorted data — just copy)
    this.references$.pipe(takeUntil(this.destroy$)).subscribe((references) => {
      this.sortedReferences = [...references];
      this.selection.setRows(this.sortedReferences);
    });

    // Debounced search input
    this.searchControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((value) => {
      this.onSearch(value ?? '');
    });

    // Refresh list after single delete success
    this.actions$.pipe(ofType(DeleteExternalReferenceSuccess), takeUntil(this.destroy$)).subscribe(() => {
      this.selection.clear();
      this.loadReferences();
    });

    // Refresh list after bulk delete / create / update success
    this.actions$
      .pipe(ofType(BulkDeleteExternalReferencesSuccess, CreateExternalReferenceSuccess, UpdateExternalReferenceSuccess), takeUntil(this.destroy$))
      .subscribe(() => {
        this.selection.clear();
        this.loadReferences();
      });

    // Initial load
    this.loadReferences();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Data loading ──────────────────────────────────────────────────────────

  loadReferences(): void {
    this.store.dispatch(
      LoadExternalReferences({
        page: this.page,
        key: this.keyword || undefined,
        filters: this.filters,
        sortBy: this.sortField || undefined,
        sortMode: this.sortField ? this.sortMode : undefined,
      }),
    );
  }

  toPage(page: IPageCount): void {
    this.page = { ...this.page, number: page.number, size: page.size };
    this.loadReferences();
  }

  // ── Search ────────────────────────────────────────────────────────────────

  onSearch(value: string): void {
    this.keyword = value;
    this.store.dispatch(SetExternalReferenceSearch({ keyword: value }));
    this.page = { ...this.page, number: 1 };
    this.loadReferences();
  }

  // ── Filters ───────────────────────────────────────────────────────────────

  private get extRefFilterConfig(): FilterDialogData {
    return {
      config: {
        title: 'knowledge_base.external_references.filter.title',
        sections: [
          {
            title: 'knowledge_base.external_references.filter.group_title',
            fields: [
              {
                key: 'product',
                label: 'filter.product',
                type: 'autocomplete',
                placeholder: 'filter.product_placeholder',
                searchFn: (term: string): Observable<FilterFieldOption[]> =>
                  this.productsService.fetchProductsBySearch({ number: 1, size: 20 }, { filters: [] }, term, undefined, false, true).pipe(
                    map((res) =>
                      (res.data ?? [])
                        .filter((p) => p.id != null && p.sku)
                        .map((p) => ({
                          value: p.id!,
                          label: `${p.name} (${p.sku})`,
                        })),
                    ),
                  ),
              },
              {
                key: 'customer',
                label: 'filter.customer',
                type: 'autocomplete',
                placeholder: 'filter.customer_placeholder',
                searchFn: (term: string): Observable<FilterFieldOption[]> =>
                  this.customersService
                    .listCustomers({ number: 1, size: 20 }, undefined, term)
                    .pipe(map((res) => (res.data ?? []).map((c) => ({ value: c.id, label: c.name })))),
              },
              {
                key: 'source',
                label: 'knowledge_base.external_references.col.source',
                type: 'single-select',
                options: [
                  { value: 'import', label: 'knowledge_base.external_references.source.import' },
                  { value: 'manual', label: 'knowledge_base.external_references.source.manual' },
                  { value: 'rule', label: 'knowledge_base.external_references.source.rule' },
                ],
              },
              {
                key: 'create_date',
                label: 'filter.created_date',
                type: 'date-range',
                minKey: 'create_date_from',
                maxKey: 'create_date_to',
                minPlaceholder: 'common.from',
                maxPlaceholder: 'common.to',
              },
            ],
          },
        ],
      },
      values: {},
    };
  }

  setFilter(): void {
    const dialogRef = this.dialog.open(FilterDialogComponent, {
      data: {
        ...this.extRefFilterConfig,
        values: { ...this.activeFilterValues },
      } as FilterDialogData,
      width: '900px',
    });

    dialogRef.afterClosed().subscribe((result: Record<string, any> | undefined) => {
      if (result) {
        this.activeFilterValues = result;
        this.filters = {
          source: (result['source'] || '') as ExternalReferenceFilters['source'],
          customer_id: result['customer']?.value ?? null,
          product_id: result['product']?.value ?? null,
          create_date_from: result['create_date_from'] || null,
          create_date_to: result['create_date_to'] || null,
        };
        this.store.dispatch(SetExternalReferenceFilters({ filters: this.filters }));
        this.page = { ...this.page, number: 1 };
        this.loadReferences();
      }
    });
  }

  hasActiveFilters(): boolean {
    return !!(
      this.filters.source ||
      this.filters.customer_id ||
      this.filters.product_id ||
      this.filters.create_date_from ||
      this.filters.create_date_to
    );
  }

  clearFilter(): void {
    this.activeFilterValues = {};
    this.filters = { source: '', customer_id: null, product_id: null, create_date_from: null, create_date_to: null };
    this.store.dispatch(SetExternalReferenceFilters({ filters: this.filters }));
    this.page = { ...this.page, number: 1 };
    this.loadReferences();
  }

  // ── Sorting (server-side) ─────────────────────────────────────────────────

  onSort(field: SortableField): void {
    if (this.sortField === field) {
      this.sortMode = this.sortMode === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortMode = 'asc';
    }
    this.page = { ...this.page, number: 1 };
    this.loadReferences();
  }

  // ── Checkbox selection ────────────────────────────────────────────────────


  // ── Dialogs ───────────────────────────────────────────────────────────────

  openCreateDialog(): void {
    this.dialog.open(AddExternalReferenceComponent, {
      data: null,
      disableClose: true,
    });
  }

  openEditDialog(reference: IExternalReference): void {
    this.dialog.open(AddExternalReferenceComponent, {
      data: reference,
      disableClose: true,
    });
  }

  openImportDialog(): void {
    const dialogRef = this.dialog.open(ImportKnowledgeBaseDialogComponent, {
      data: { type: 'external-reference' },
      disableClose: true,
      width: '700px',
      maxWidth: '95vw',
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.loadReferences();
      }
    });
  }

  deleteReference(reference: IExternalReference): void {
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.remove'),
      },
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.store.dispatch(DeleteExternalReference({ id: reference.id }));
      }
    });
  }

  bulkDelete(): void {
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.remove'),
      },
    });
    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.store.dispatch(BulkDeleteExternalReferences({ ids: this.selection.ids }));
      }
    });
  }

  // ── Export ──────────────────────────────────────────────────────────────────

  exportList(): void {
    this.externalReferenceService
      .exportExternalReferences(
        'xlsx',
        this.keyword || undefined,
        this.filters,
        this.sortField || undefined,
        this.sortField ? this.sortMode : undefined,
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `external_references_${new Date().toISOString().split('T')[0]}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      });
  }

  // ── Helpers ───────────────────────────────────────────────────────────────

  getProductLink(reference: IExternalReference): string {
    return `/inventory/${reference.product_id}`;
  }

  hasInvalidSku(reference: IExternalReference): boolean {
    return !reference.product_id;
  }

}
